import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { getBlockRealId } from '@/components/preview/block-selection/blockClipboard';
import { resolveScopeInsertIndex } from '@/components/preview/block-selection/blockGroups';
import { findBlockElement } from '@/components/preview/pagination/utils';
import { instrumentBlocksFromGrid } from '@/components/preview/paste/blockFromClipboard';
import { TestDefaultBlock } from '@/components/reports/types';
import FreeInstrumentModal, {
    type FreeInstrumentValues,
} from '@/components/wizard/FreeInstrumentModal';
import PasteInstrumentModal from '@/components/wizard/PasteInstrumentModal';
import TestSelector, {
    type InstrumentFallbackRoute,
    type TestSelectorFilters,
} from '@/components/wizard/TestSelector';
import { FREE_INSTRUMENT_LABEL } from '@/constants';
import { useModal } from '@/hooks/use-modal';
import {
    buildTableChartMultiDatapoints,
    extractTableChartData,
    getColumnLabelById,
    getSafeTableChartSelection,
} from '@/lib/table-chart';
import {
    createId,
    createTableRow,
    createTextBlock,
    normalizeTableBlock,
    normalizeTableRow,
} from '@/lib/table-utils';
import { ReportData } from '@/types/report';
import {
    ChartAxisConfig,
    ChartBlock,
    ChartCutoffLine,
    ChartReferenceBand,
    ChartType,
    TableBlock,
    TableReference,
    TestBlock,
    TextBlock,
} from '@/types/report-payload';
import { resolvePatientAge } from '@/utils/resolvePatientAge';
import { PanelScope } from '../context/types';
import { generateNewBlock } from '../utils/generateNewBlock';
import type { ModalSize } from '@/components/ui/modal-provider';

const mapDefaultBlocksToTableBlocks = (
    defaultBlocks: TestDefaultBlock[],
    testName?: string,
    sourceTestId?: string,
): TableBlock[] => {
    const baseId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const normalizedName = testName?.trim() || '';

    return defaultBlocks.map((block, index) => {
        const headerCellCount = block.header?.cells?.length || 0;
        const rowCellCount = Math.max(
            0,
            ...(block.rows || []).map((row) => row.cells?.length || 0),
        );
        const columnCount = block.columnWidths?.length || Math.max(headerCellCount, rowCellCount);

        const header = normalizeTableRow(block.header || {}, columnCount);
        const rows = (block.rows || []).map((row) => {
            const normalizedRow = normalizeTableRow(row, columnCount);
            if (!sourceTestId) return normalizedRow;

            return {
                ...normalizedRow,
                source_test_id: sourceTestId,
                source_provider: 'satepsi' as const,
            };
        });

        const columnWidths =
            block.columnWidths?.length === columnCount
                ? block.columnWidths
                : new Array(columnCount).fill(150);

        return {
            id: `${baseId}-${index + 1}`,
            block_type: 'table',
            order: index,
            has_content: true,
            automatic: false,
            description: normalizedName,
            header,
            rows,
            columnWidths,
            instrument_source: 'satepsi',
            references: sourceTestId
                ? [
                      {
                          source_test_id: sourceTestId,
                          source_provider: 'satepsi',
                      },
                  ]
                : [],
        } as TableBlock;
    });
};

const createTableReference = (testId?: string): TableReference[] => {
    if (!testId) return [];

    return [
        {
            source_test_id: testId,
            source_provider: 'satepsi',
        },
    ];
};

const withTrailingTextBlocks = (blocks: TableBlock[]): TestBlock[] => {
    return blocks;
};

/**
 * Builds the block group for instruments picked without a target table (the
 * "Adicionar instrumento" entry points). Each instrument becomes its tables
 * followed by one linked interpretation text block, so the group behaves as a
 * unit for selection, cascade-delete and reorder.
 */
const buildBlocksForSelectedTests = (selectedTests: SelectedTest[]): TestBlock[] => {
    const blocks: TestBlock[] = [];

    selectedTests.forEach((test) => {
        if (Array.isArray(test.defaultBlocks) && test.defaultBlocks.length > 0) {
            const tables = mapDefaultBlocksToTableBlocks(test.defaultBlocks, test.name, test.id);
            blocks.push(...tables);
            const lastTable = tables[tables.length - 1];
            if (lastTable?.id) {
                blocks.push(createTextBlock(lastTable.id));
            }
            return;
        }

        const table = generateNewBlock('tool_table', blocks.length) as TableBlock;
        const seededRow = {
            ...createTableRow(table.columnWidths.length, [test.name || '']),
            ...(test.id ? { source_test_id: test.id, source_provider: 'satepsi' as const } : {}),
        };
        const instrumentTable: TableBlock = {
            ...table,
            description: test.name?.trim() || '',
            instrument_source: 'satepsi',
            rows: [seededRow],
            references: createTableReference(test.id),
        };

        blocks.push(instrumentTable);
        blocks.push(createTextBlock(instrumentTable.id));
    });

    return blocks;
};

/**
 * Blank counterpart of `buildBlocksForSelectedTests`: the same group shape
 * (table + linked interpretation) with nothing filled in, for an instrument
 * that is not in the Satepsi catalog.
 */
const buildFreeInstrumentBlocks = (values: FreeInstrumentValues): TestBlock[] => {
    const table = generateNewBlock('tool_table', 0) as TableBlock;
    // Built fresh from the modal's columns, so this never aliases the shared
    // DEFAULT_COLUMN_WIDTHS array that generateNewBlock hands back.
    const columnWidths = values.columns.map((column) => column.width);
    const reference = values.reference.trim();

    const instrumentTable: TableBlock = {
        ...table,
        description: values.name.trim() || FREE_INSTRUMENT_LABEL,
        instrument_source: 'free_mode',
        // Omitted rather than stored empty, so a report without a citation has
        // the same payload it had before the field existed.
        ...(reference ? { instrument_reference: reference } : {}),
        header: createTableRow(
            columnWidths.length,
            values.columns.map((column) => column.label),
        ),
        // A rowless table renders as the "selecione instrumentos" placeholder
        // (TestTable), which is the catalog the user just declined. One blank
        // row makes it open as a real, editable table instead.
        rows: [createTableRow(columnWidths.length)],
        columnWidths,
    };

    return [instrumentTable, createTextBlock(instrumentTable.id)];
};

type OpenTestSelectorOptions = {
    /** A table to fill, or `null` to build a fresh instrument group. */
    blockId: string | number | null;
    mode?: 'default' | 'inline';
    /** Overrides append-at-end for the picked tests (the page context menu). */
    insertBlocks?: (blocks: TestBlock[]) => void;
    /**
     * Where a *new* group belongs, for the free and pasted routes. Separate
     * from `insertBlocks` on purpose: that callback is the clipboard's own
     * inserter, which announces "blocos colados" and skips the panel scope —
     * both wrong for an instrument the user is about to describe by hand.
     */
    anchorRealId?: string | null;
    /** Rebuilds a catalog view the user left through one of the other routes. */
    initialFilters?: TestSelectorFilters;
};

const getLinkedTextBlockIndex = (blocks: TestBlock[], tableId?: string) => {
    if (!tableId) return -1;

    return blocks.findIndex((block) => {
        return block.block_type === 'text' && (block as TextBlock).linked_table_id === tableId;
    });
};

const ensureLinkedTextBlock = (
    blocks: TestBlock[],
    tableIndex: number,
    tableId?: string,
): TestBlock[] => {
    if (!tableId || getLinkedTextBlockIndex(blocks, tableId) !== -1) {
        return blocks;
    }

    const updatedBlocks = [...blocks];
    updatedBlocks.splice(tableIndex + 1, 0, createTextBlock(tableId));
    return updatedBlocks;
};

const replaceTableGroup = (
    blocks: TestBlock[],
    tableIndex: number,
    replacementBlocks: TestBlock[],
): TestBlock[] => {
    const targetBlock = blocks[tableIndex];
    if (targetBlock?.block_type !== 'table') return blocks;

    const linkedTextIndex = getLinkedTextBlockIndex(blocks, targetBlock.id);
    const indexesToRemove = new Set<number>([tableIndex]);
    if (linkedTextIndex !== -1) {
        indexesToRemove.add(linkedTextIndex);
    }

    blocks.forEach((block, index) => {
        if (
            block.block_type === 'chart' &&
            targetBlock.id &&
            (block as ChartBlock).linked_table_id === targetBlock.id
        ) {
            indexesToRemove.add(index);
        }
    });

    const removalIndexes = Array.from(indexesToRemove).sort((a, b) => a - b);
    const insertionIndex = tableIndex - removalIndexes.filter((index) => index < tableIndex).length;

    const updatedBlocks = blocks.filter((_, index) => !indexesToRemove.has(index));
    updatedBlocks.splice(insertionIndex, 0, ...replacementBlocks);

    return updatedBlocks;
};

/**
 * Scrolls the preview to a freshly created block (or to the end as fallback).
 * Centred, not `start`: a block flush against the top edge of the scroller reads
 * as clipped, and the user has no idea whether anything else came with it.
 */
const scrollPreviewToBlock = (blockId: string | null) => {
    setTimeout(() => {
        const previewContainer = document.querySelector('[data-preview-container]');
        if (!previewContainer) return;

        const blockEl = blockId ? findBlockElement(previewContainer, blockId) : null;
        if (blockEl) {
            blockEl.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            return;
        }

        previewContainer.scrollTo({
            top: previewContainer.scrollHeight,
            behavior: 'smooth',
        });
    }, 100);
};

const insertChartAfterTable = (blocks: TestBlock[], tableId: string, chartBlock: ChartBlock) => {
    const tableIndex = blocks.findIndex(
        (block) => block.block_type === 'table' && block.id === tableId,
    );

    if (tableIndex === -1) {
        return [...blocks, chartBlock];
    }

    let insertionIndex = tableIndex + 1;
    while (
        insertionIndex < blocks.length &&
        blocks[insertionIndex].block_type === 'chart' &&
        (blocks[insertionIndex] as ChartBlock).linked_table_id === tableId
    ) {
        insertionIndex += 1;
    }

    const updatedBlocks = [...blocks];
    updatedBlocks.splice(insertionIndex, 0, chartBlock);
    return updatedBlocks;
};

interface ChartConfigValues {
    chartType: ChartType;
    linkedTableId: string;
    xAxisColumnId: string;
    yAxisColumnIds: string[];
    measurementType: string;
    title?: string;
    referenceBands?: ChartReferenceBand[];
    cutoffLines?: ChartCutoffLine[];
    axis?: ChartAxisConfig;
    quadrant?: { x?: number; y?: number };
    axisEndLabels?: ChartBlock['axis_end_labels'];
    seriesColors?: Record<string, string>;
    seriesSymbols?: Record<string, string>;
    groupColumnId?: string;
    colorByGroup?: boolean;
    colorByClassification?: boolean;
    highlight?: ChartBlock['highlight'];
    showDeltas?: boolean;
    lineStyle?: ChartBlock['line_style'];
    barStyle?: ChartBlock['bar_style'];
    pointLabels?: boolean;
    referenceColumnIds?: string[];
    bandColor?: string;
    excludedXLabels?: string[];
    pointColors?: Record<string, string>;
    datapoints: ChartBlock['datapoints'];
}

/** Annotate the datapoints cache with the chosen series colors (by column order). */
const applySeriesColorsToDatapoints = (
    datapoints: ChartBlock['datapoints'],
    yAxisColumnIds: string[],
    seriesColors?: Record<string, string>,
): ChartBlock['datapoints'] => {
    if (!datapoints?.series?.length || !seriesColors) return datapoints;
    return {
        ...datapoints,
        series: datapoints.series.map((series, index) => {
            const color = seriesColors[yAxisColumnIds[index]];
            return color ? { ...series, color } : series;
        }),
    };
};

interface SelectedTest {
    id?: string;
    name?: string;
    domain?: string[] | string;
    defaultBlocks?: TestDefaultBlock[];
}

const createLinkedChartBlock = (order: number, config: ChartConfigValues): ChartBlock => {
    return {
        id: createId('block'),
        block_type: 'chart',
        order,
        has_content: true,
        automatic: false,
        chart_type: config.chartType,
        source_mode: 'linked_table',
        title: config.title?.trim() || undefined,
        measurement_type: config.measurementType,
        linked_table_id: config.linkedTableId,
        source: {
            x_axis_column_id: config.xAxisColumnId,
            // Keep the scalar id = first column for legacy readers.
            y_axis_column_id: config.yAxisColumnIds[0] ?? '',
            y_axis_column_ids: config.yAxisColumnIds,
            skip_empty: true,
            series_colors: config.seriesColors,
            reference_column_ids: config.referenceColumnIds,
            band_color: config.bandColor,
            series_symbols: config.seriesSymbols,
            group_column_id: config.groupColumnId,
            excluded_x_labels: config.excludedXLabels,
            point_colors: config.pointColors,
        },
        reference_bands: config.referenceBands,
        cutoff_lines: config.cutoffLines,
        axis: config.axis,
        quadrant: config.quadrant,
        axis_end_labels: config.axisEndLabels,
        line_style: config.lineStyle,
        bar_style: config.barStyle,
        // Stored only when disabled — undefined means "labels on" (the default).
        point_labels: config.pointLabels === false ? false : undefined,
        // Stored only when enabled — undefined means "single color family".
        color_by_group: config.colorByGroup === true ? true : undefined,
        color_by_classification: config.colorByClassification === true ? true : undefined,
        highlight: config.highlight,
        show_deltas: config.showDeltas === true ? true : undefined,
        datapoints: applySeriesColorsToDatapoints(
            config.datapoints,
            config.yAxisColumnIds,
            config.seriesColors,
        ),
    };
};

const resolveSingleBlockReference = (
    currentBlock: TableBlock,
    selectedTests: Array<{ id?: string }>,
): TableReference[] => {
    if (Array.isArray(currentBlock.references) && currentBlock.references.length > 0) {
        const existing = currentBlock.references[0];
        if (existing?.source_test_id) {
            return [
                {
                    source_test_id: existing.source_test_id,
                    source_provider: 'satepsi',
                },
            ];
        }
    }

    return createTableReference(selectedTests[0]?.id);
};

const isPlaceholderTableBlock = (block: TestBlock) => {
    if (block.block_type !== 'table') return false;
    return !Array.isArray(block.rows) || block.rows.length === 0;
};

const addSatepsiRowTestId = (
    ids: Set<string>,
    row?: { source_test_id?: string; source_provider?: string },
) => {
    if (!row?.source_test_id) return;
    if (row.source_provider && row.source_provider !== 'satepsi') {
        return;
    }

    ids.add(row.source_test_id);
};

const collectExistingSatepsiTestIds = (blocks: TestBlock[] = []) => {
    const ids = new Set<string>();

    blocks.forEach((block) => {
        if (block.block_type !== 'table') return;

        block.rows?.forEach((row) => {
            addSatepsiRowTestId(ids, row);
        });
    });

    return ids;
};

const getHeaderLabels = (header?: TableBlock['header']) => {
    if (!header?.cells?.length) return [];
    const idToIndex = new Map<string, number>();
    header.cells.forEach((cell, index) => {
        idToIndex.set(cell.id, index);
    });

    return header.cells.map((cell) => {
        if (cell.mergedInto) {
            const originIndex = idToIndex.get(cell.mergedInto);
            if (originIndex !== undefined) {
                return header.cells[originIndex].value;
            }
        }
        return cell.value;
    });
};

export function useReportTests(
    formData: Partial<ReportData>,
    handleUpdate: (
        updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>),
    ) => void,
    /**
     * Hands a freshly created block to the preview so it opens straight into
     * edit mode. Optional so callers that only need the modals can skip it.
     */
    requestBlockEdit?: (blockId: string | null) => void,
    /**
     * Points the tools panel at a group. Optional for the same reason as
     * `requestBlockEdit`: callers that only need the modals can skip it.
     */
    setPanelScope?: (scope: PanelScope | null) => void,
) {
    const { openWizardModal, closeModal } = useModal();
    const [showChartConfig, setShowChartConfig] = useState(false);
    const [editingChartBlock, setEditingChartBlock] = useState<ChartBlock | null>(null);
    const [chartSourceTableId, setChartSourceTableId] = useState<string | null>(null);
    const [lockChartSourceSelection, setLockChartSourceSelection] = useState(false);

    // Refs to keep data fresh inside callbacks/closures
    // Although we use functional updates now, the test selector might need age from formData
    const formDataRef = useRef(formData);
    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    const availableChartTables = useMemo(() => {
        return (formData.tests?.blocks || []).filter((block) => {
            return block.block_type === 'table' && !!block.id;
        }) as TableBlock[];
    }, [formData.tests?.blocks]);

    const chartModalInitialConfig = useMemo(() => {
        const source = editingChartBlock?.source;
        // Back-compat: legacy blocks only have the scalar y_axis_column_id.
        const yAxisColumnIds = source?.y_axis_column_ids?.length
            ? source.y_axis_column_ids
            : source?.y_axis_column_id
              ? [source.y_axis_column_id]
              : undefined;
        return {
            chartType: editingChartBlock?.chart_type || 'bar',
            linkedTableId: editingChartBlock?.linked_table_id,
            measurementType: editingChartBlock?.measurement_type,
            xAxisColumnId: source?.x_axis_column_id,
            yAxisColumnIds,
            title: editingChartBlock?.title,
            referenceBands: editingChartBlock?.reference_bands,
            cutoffLines: editingChartBlock?.cutoff_lines,
            axis: editingChartBlock?.axis,
            quadrant: editingChartBlock?.quadrant,
            axisEndLabels: editingChartBlock?.axis_end_labels,
            seriesColors: source?.series_colors,
            seriesSymbols: source?.series_symbols,
            groupColumnId: source?.group_column_id,
            colorByGroup: editingChartBlock?.color_by_group,
            colorByClassification: editingChartBlock?.color_by_classification,
            highlight: editingChartBlock?.highlight,
            showDeltas: editingChartBlock?.show_deltas,
            lineStyle: editingChartBlock?.line_style,
            barStyle: editingChartBlock?.bar_style,
            pointLabels: editingChartBlock?.point_labels,
            referenceColumnIds: source?.reference_column_ids,
            bandColor: source?.band_color,
            excludedXLabels: source?.excluded_x_labels,
            pointColors: source?.point_colors,
        };
    }, [editingChartBlock]);

    const closeChartConfig = useCallback(() => {
        setShowChartConfig(false);
        setEditingChartBlock(null);
        setChartSourceTableId(null);
        setLockChartSourceSelection(false);
    }, []);

    const resolveTableId = useCallback((blockIdentifier: string | number) => {
        const blocks = formDataRef.current.tests?.blocks || [];

        const tableBlock = blocks.find((block) => {
            if (block.block_type !== 'table') return false;

            if (typeof blockIdentifier === 'string') {
                return block.id === blockIdentifier;
            }

            return block.order === blockIdentifier;
        });

        return tableBlock?.id || null;
    }, []);

    const handleAddSelectedTests = useCallback(
        (
            selectedTests: SelectedTest[],
            blockIdentifier: string | number | null,
            mode: 'default' | 'inline' = 'default',
        ) => {
            if (blockIdentifier === null) return;

            handleUpdate((prev) => {
                const testsSection = prev.tests || {
                    section_title: '',
                    blocks: [],
                };
                const blocks = [...(testsSection.blocks || [])];

                let blockIndex = -1;
                if (typeof blockIdentifier === 'string') {
                    blockIndex = blocks.findIndex((b) => b.id === blockIdentifier);
                    // Fallback: try parsing as number if it looks like one (legacy order passed as string)
                    if (blockIndex === -1 && !isNaN(parseInt(blockIdentifier))) {
                        blockIndex = blocks.findIndex((b) => b.order === parseInt(blockIdentifier));
                    }
                } else {
                    blockIndex = blocks.findIndex((b) => b.order === blockIdentifier);
                }

                if (blockIndex === -1) return {};

                const block = blocks[blockIndex];
                if (block.block_type === 'table') {
                    const tableBlock = normalizeTableBlock(block as TableBlock);
                    const createSatepsiRow = (
                        values: Array<string | number | null>,
                        testId?: string,
                    ) => {
                        const row = createTableRow(tableBlock.columnWidths.length, values);
                        if (!testId) return row;

                        return {
                            ...row,
                            source_test_id: testId,
                            source_provider: 'satepsi' as const,
                        };
                    };
                    const headerLabels = getHeaderLabels(tableBlock.header).map((label) =>
                        label.toLowerCase(),
                    );
                    const shouldSetDescription =
                        selectedTests.length === 1 && !tableBlock.description?.trim();
                    const selectedTestName = selectedTests[0]?.name?.trim() || '';

                    if (mode === 'inline') {
                        const testsWithDefaults = selectedTests.filter((test) => {
                            return (
                                Array.isArray(test?.defaultBlocks) && test.defaultBlocks.length > 0
                            );
                        });
                        const testsWithoutDefaults = selectedTests.filter((test) => {
                            return (
                                !Array.isArray(test?.defaultBlocks) ||
                                test.defaultBlocks.length === 0
                            );
                        });
                        let updatedBlocks = [...blocks];

                        if (testsWithoutDefaults.length > 0) {
                            const newRows = testsWithoutDefaults.map((test) => {
                                const values = headerLabels.map((label) => {
                                    if (label.includes('instrumento') || label.includes('teste'))
                                        return test.name || '';
                                    if (label.includes('domínio') || label.includes('dominio'))
                                        return Array.isArray(test.domain)
                                            ? test.domain.join(', ')
                                            : test.domain || '';
                                    return '';
                                });

                                return createSatepsiRow(values, test.id);
                            });

                            let nextTableBlock: TableBlock = {
                                ...tableBlock,
                                rows: [...tableBlock.rows, ...newRows],
                                references: resolveSingleBlockReference(tableBlock, selectedTests),
                            };

                            if (
                                shouldSetDescription &&
                                testsWithoutDefaults.length === 1 &&
                                selectedTestName
                            ) {
                                nextTableBlock = {
                                    ...nextTableBlock,
                                    description: selectedTestName,
                                };
                            }

                            updatedBlocks[blockIndex] = nextTableBlock;
                        }

                        if (testsWithDefaults.length > 0) {
                            const mappedBlocks = withTrailingTextBlocks(
                                testsWithDefaults.flatMap((test) => {
                                    return mapDefaultBlocksToTableBlocks(
                                        test.defaultBlocks || [],
                                        test.name,
                                        test.id,
                                    );
                                }),
                            );

                            const shouldReplacePlaceholder =
                                testsWithoutDefaults.length === 0 &&
                                isPlaceholderTableBlock(tableBlock);

                            if (shouldReplacePlaceholder) {
                                updatedBlocks = replaceTableGroup(
                                    updatedBlocks,
                                    blockIndex,
                                    mappedBlocks,
                                );
                            } else {
                                updatedBlocks.splice(blockIndex + 1, 0, ...mappedBlocks);
                            }
                        }

                        if (
                            testsWithoutDefaults.length > 0 &&
                            isPlaceholderTableBlock(tableBlock)
                        ) {
                            updatedBlocks = ensureLinkedTextBlock(
                                updatedBlocks,
                                blockIndex,
                                tableBlock.id,
                            );
                        }

                        const reindexedBlocks = updatedBlocks.map((currentBlock, index) => ({
                            ...currentBlock,
                            order: index,
                        }));

                        return {
                            tests: {
                                ...testsSection,
                                blocks: reindexedBlocks,
                            },
                        };
                    }

                    const defaultBlocks =
                        selectedTests.length === 1 ? selectedTests[0]?.defaultBlocks : undefined;

                    if (
                        selectedTests.length === 1 &&
                        Array.isArray(defaultBlocks) &&
                        defaultBlocks.length > 0
                    ) {
                        const mappedBlocks = withTrailingTextBlocks(
                            mapDefaultBlocksToTableBlocks(
                                defaultBlocks,
                                selectedTestName,
                                selectedTests[0]?.id,
                            ),
                        );
                        let updatedBlocks = [...blocks];
                        updatedBlocks = replaceTableGroup(updatedBlocks, blockIndex, mappedBlocks);
                        const reindexedBlocks = updatedBlocks.map((currentBlock, index) => ({
                            ...currentBlock,
                            order: index,
                        }));

                        return {
                            tests: {
                                ...testsSection,
                                blocks: reindexedBlocks,
                            },
                        };
                    }

                    if (selectedTests.length > 1) {
                        const testsWithDefaults = selectedTests.filter((test) => {
                            return (
                                Array.isArray(test?.defaultBlocks) && test.defaultBlocks.length > 0
                            );
                        });
                        const testsWithoutDefaults = selectedTests.filter((test) => {
                            return (
                                !Array.isArray(test?.defaultBlocks) ||
                                test.defaultBlocks.length === 0
                            );
                        });
                        if (testsWithDefaults.length > 0) {
                            let updatedBlocks = [...blocks];

                            if (testsWithoutDefaults.length > 0) {
                                const newRows = testsWithoutDefaults.map((test) => {
                                    const values = headerLabels.map((label) => {
                                        if (
                                            label.includes('instrumento') ||
                                            label.includes('teste')
                                        )
                                            return test.name || '';
                                        if (label.includes('domínio') || label.includes('dominio'))
                                            return Array.isArray(test.domain)
                                                ? test.domain.join(', ')
                                                : test.domain || '';
                                        return '';
                                    });

                                    return createSatepsiRow(values, test.id);
                                });

                                updatedBlocks[blockIndex] = {
                                    ...tableBlock,
                                    rows: [...tableBlock.rows, ...newRows],
                                    references: resolveSingleBlockReference(
                                        tableBlock,
                                        selectedTests,
                                    ),
                                };
                            }

                            const mappedBlocks = withTrailingTextBlocks(
                                testsWithDefaults.flatMap((test) => {
                                    return mapDefaultBlocksToTableBlocks(
                                        test.defaultBlocks || [],
                                        test.name,
                                        test.id,
                                    );
                                }),
                            );

                            const shouldReplacePlaceholder =
                                testsWithoutDefaults.length === 0 &&
                                isPlaceholderTableBlock(tableBlock);

                            if (shouldReplacePlaceholder) {
                                updatedBlocks = replaceTableGroup(
                                    updatedBlocks,
                                    blockIndex,
                                    mappedBlocks,
                                );
                            } else {
                                updatedBlocks.splice(blockIndex + 1, 0, ...mappedBlocks);
                            }

                            if (
                                testsWithoutDefaults.length > 0 &&
                                isPlaceholderTableBlock(tableBlock)
                            ) {
                                updatedBlocks = ensureLinkedTextBlock(
                                    updatedBlocks,
                                    blockIndex,
                                    tableBlock.id,
                                );
                            }

                            const reindexedBlocks = updatedBlocks.map((currentBlock, index) => ({
                                ...currentBlock,
                                order: index,
                            }));

                            return {
                                tests: {
                                    ...testsSection,
                                    blocks: reindexedBlocks,
                                },
                            };
                        }
                    }

                    const newRows = selectedTests.map((test) => {
                        const values = headerLabels.map((label) => {
                            if (label.includes('instrumento') || label.includes('teste'))
                                return test.name || '';
                            if (label.includes('domínio') || label.includes('dominio'))
                                return Array.isArray(test.domain)
                                    ? test.domain.join(', ')
                                    : test.domain || '';
                            return '';
                        });

                        return createSatepsiRow(values, test.id);
                    });

                    let nextTableBlock: TableBlock = {
                        ...tableBlock,
                        rows: [...tableBlock.rows, ...newRows],
                        references: resolveSingleBlockReference(tableBlock, selectedTests),
                    };

                    if (shouldSetDescription && selectedTestName) {
                        nextTableBlock = {
                            ...nextTableBlock,
                            description: selectedTestName,
                        };
                    }

                    let updatedBlocks = [...blocks];
                    updatedBlocks[blockIndex] = nextTableBlock;

                    if (isPlaceholderTableBlock(tableBlock)) {
                        updatedBlocks = ensureLinkedTextBlock(
                            updatedBlocks,
                            blockIndex,
                            nextTableBlock.id,
                        );
                    }

                    return {
                        tests: {
                            ...testsSection,
                            blocks: updatedBlocks,
                        },
                    };
                }
                return {};
            });
        },
        [handleUpdate],
    );

    // Keep a ref to the handler to avoid stale closures in the modal callback
    const handleAddSelectedTestsRef = useRef(handleAddSelectedTests);
    useEffect(() => {
        handleAddSelectedTestsRef.current = handleAddSelectedTests;
    }, [handleAddSelectedTests]);

    const handleCreateTestBlocks = useCallback(
        (selectedTests: SelectedTest[], insertBlocks?: (blocks: TestBlock[]) => void) => {
            const newBlocks = buildBlocksForSelectedTests(selectedTests);
            if (newBlocks.length === 0) return;

            if (insertBlocks) {
                insertBlocks(newBlocks);
                return;
            }

            handleUpdate((prev) => {
                const currentTests = prev.tests || {
                    section_title: '',
                    blocks: [],
                };
                const updatedBlocks = [...(currentTests.blocks || []), ...newBlocks].map(
                    (block, index) => ({
                        ...block,
                        order: index,
                    }),
                );

                return {
                    tests: {
                        ...currentTests,
                        blocks: updatedBlocks,
                    },
                };
            });

            scrollPreviewToBlock(newBlocks[0]?.id || null);
        },
        [handleUpdate],
    );

    const handleCreateTestBlocksRef = useRef(handleCreateTestBlocks);
    useEffect(() => {
        handleCreateTestBlocksRef.current = handleCreateTestBlocks;
    }, [handleCreateTestBlocks]);

    const openTestSelector = (options: OpenTestSelectorOptions) => {
        const { blockId, mode = 'default', insertBlocks, anchorRealId = null } = options;
        const patient = formDataRef.current.patient;
        const examDate = formDataRef.current.tests_performed_at_start;
        const patientAge = resolvePatientAge({
            dateOfBirth: patient?.date_of_birth,
            examDate,
            fallbackAge: patient?.age,
        });
        const alreadyAddedTestIds = collectExistingSatepsiTestIds(
            formDataRef.current.tests?.blocks || [],
        );

        openWizardModal({
            title: 'Adicionar Instrumentos',
            size: 'lg',
            content: (
                <TestSelector
                    alreadyAddedTestIds={alreadyAddedTestIds}
                    onSelectTests={(tests) => {
                        if (blockId === null) {
                            handleCreateTestBlocksRef.current(tests, insertBlocks);
                            return;
                        }
                        handleAddSelectedTestsRef.current(tests, blockId, mode);
                    }}
                    onClose={closeModal}
                    patientAge={patientAge}
                    initialFilters={options.initialFilters}
                    // Withheld when the picker is filling a table that already
                    // exists: the free and pasted routes build a group of their
                    // own, which would leave that empty table stranded above it.
                    onFallbackRoute={
                        blockId === null
                            ? (route, filters) =>
                                  openInstrumentFallback(route, filters, {
                                      blockId,
                                      mode,
                                      insertBlocks,
                                      anchorRealId,
                                  })
                            : undefined
                    }
                />
            ),
        });
    };

    /**
     * The catalog dead-ended, so it hands the user to the route they picked.
     *
     * This does not close anything and does not open a second dialog: the modal
     * provider holds one slot, so writing to it swaps the content of the dialog
     * that is already on screen. `size` stays `lg` for the same reason — at
     * `md` the box would visibly shrink and re-centre mid-swap.
     *
     * `selectorOptions` travels along so "Voltar" can rebuild the exact catalog
     * view that was left behind, filters and all.
     */
    const openInstrumentFallback = (
        route: InstrumentFallbackRoute,
        filters: TestSelectorFilters,
        selectorOptions: OpenTestSelectorOptions,
    ) => {
        openInstrumentRoute(route, {
            anchorRealId: selectorOptions.anchorRealId ?? null,
            // Stays `lg`, the catalog's own size: at `md` the box would visibly
            // shrink and re-centre in the middle of the swap.
            size: 'lg',
            initialName: filters.query,
            backAction: {
                label: 'Voltar',
                onClick: () => openTestSelector({ ...selectorOptions, initialFilters: filters }),
            },
        });
    };

    /**
     * Drops an already-built instrument group into the section and follows it
     * with the panel.
     *
     * Shared by the two entry points that build their own blocks instead of
     * picking from the catalog — the free-instrument form and the clipboard
     * paste. Everything after "here are the blocks" is identical between them,
     * and the callers differ only in what they hand in.
     *
     * `newBlocks` is built by the caller, outside the updater: the ids must
     * survive a re-run, and the scroll below needs the id it is scrolling to.
     */
    const insertInstrumentBlocks = useCallback(
        (newBlocks: TestBlock[], anchorRealId: string | null) => {
            const instrumentTable = newBlocks[0] as TableBlock;

            handleUpdate((prev) => {
                const currentTests = prev.tests || { section_title: '', blocks: [] };
                const blocks = currentTests.blocks || [];
                // A null anchor already resolves to the end of the section.
                const insertIndex = resolveScopeInsertIndex(blocks, anchorRealId);

                return {
                    tests: {
                        ...currentTests,
                        blocks: [
                            ...blocks.slice(0, insertIndex),
                            ...newBlocks,
                            ...blocks.slice(insertIndex),
                        ].map((block, index) => ({ ...block, order: index })),
                    },
                };
            });

            // The instrument the user just described is the one they are about
            // to fill in, so the panel follows it in — rather than staying on
            // the battery list, or on whichever instrument the add was scoped
            // to. The label the reconciliation effect recomputes from the
            // blocks matches this one, so the breadcrumb does not flicker.
            setPanelScope?.({
                anchorRealId: getBlockRealId(instrumentTable),
                label: instrumentTable.description || 'Instrumento sem nome',
                focusedRealId: getBlockRealId(instrumentTable),
            });

            scrollPreviewToBlock(newBlocks[0]?.id || null);
        },
        [handleUpdate, setPanelScope],
    );

    // Same stale-closure guard as handleCreateTestBlocksRef: the modal's JSX is
    // frozen at open time, so a direct closure would insert through a stale
    // state setter.
    const insertInstrumentBlocksRef = useRef(insertInstrumentBlocks);
    useEffect(() => {
        insertInstrumentBlocksRef.current = insertInstrumentBlocks;
    }, [insertInstrumentBlocks]);

    /**
     * Opens one of the two routes that build their own instrument instead of
     * picking one from the catalog.
     *
     * Both end in the same place — `insertInstrumentBlocks` — and differ only
     * in what they collect before getting there, so they share one opener: the
     * "Instrumento Livre" form, which asks for the name, the citation and the
     * columns, and "Colar uma tabela pronta", which takes the columns and the
     * scores from the clipboard and asks only for what it cannot carry.
     *
     * `anchorRealId` is captured at open time, exactly as `openTestSelector`
     * captures its insert target: the panel's focus can move before the modal
     * confirms.
     */
    const openInstrumentRoute = (
        route: InstrumentFallbackRoute,
        {
            anchorRealId,
            size,
            initialName,
            backAction,
        }: {
            anchorRealId: string | null;
            size: ModalSize;
            /** Set only when the catalog handed the user over. */
            initialName?: string;
            backAction?: { label: string; onClick: () => void };
        },
    ) => {
        openWizardModal({
            size,
            content:
                route === 'tool_test_free' ? (
                    <FreeInstrumentModal
                        onClose={closeModal}
                        initialName={initialName}
                        backAction={backAction}
                        onConfirm={(values) =>
                            insertInstrumentBlocksRef.current(
                                buildFreeInstrumentBlocks(values),
                                anchorRealId,
                            )
                        }
                    />
                ) : (
                    <PasteInstrumentModal
                        onClose={closeModal}
                        initialName={initialName}
                        backAction={backAction}
                        patientId={formDataRef.current.patient?.id}
                        onConfirm={(grid, identity) =>
                            insertInstrumentBlocksRef.current(
                                instrumentBlocksFromGrid(grid, identity),
                                anchorRealId,
                            )
                        }
                    />
                ),
        });
    };

    const openFreeInstrumentModal = (scope?: { anchorRealId: string | null }) =>
        openInstrumentRoute('tool_test_free', {
            anchorRealId: scope?.anchorRealId ?? null,
            size: 'md',
        });

    const openPasteInstrumentModal = (scope?: { anchorRealId: string | null }) =>
        openInstrumentRoute('tool_test_paste', {
            anchorRealId: scope?.anchorRealId ?? null,
            size: 'lg',
        });

    const handleAddTestsToBlock = (
        blockId?: string | number,
        mode: 'default' | 'inline' = 'default',
    ) => {
        openTestSelector({ blockId: blockId ?? null, mode });
    };

    /**
     * "Adicionar instrumento" entry point: no target table — the selector
     * creates the instrument blocks (table + linked interpretation) directly.
     * `insertBlocks` overrides the default append-at-end (used by the page
     * context menu to insert at the clicked position).
     */
    const openInstrumentSelector = (
        insertBlocks?: (blocks: TestBlock[]) => void,
        anchorRealId?: string | null,
    ) => {
        openTestSelector({ blockId: null, insertBlocks, anchorRealId });
    };

    const handleAddChartFromTable = (blockId: string | number) => {
        const resolvedTableId = resolveTableId(blockId);
        if (!resolvedTableId) return;

        setEditingChartBlock(null);
        setChartSourceTableId(resolvedTableId);
        setLockChartSourceSelection(true);
        setShowChartConfig(true);
    };

    /**
     * `anchorTableId` comes from the tools panel when it is scoped to a group:
     * the chart then opens pre-pointed at that instrument's table instead of
     * making the user pick one. The choice stays unlocked, unlike the table's
     * own chart button.
     */
    const openChartConfigFromToolbox = useCallback(
        (anchorTableId?: string | null) => {
            if (availableChartTables.length === 0) {
                toast.info('Crie uma tabela primeiro para gerar um gráfico a partir dela.');
                return;
            }

            const prefilled =
                anchorTableId && availableChartTables.some((table) => table.id === anchorTableId)
                    ? anchorTableId
                    : null;

            setEditingChartBlock(null);
            setChartSourceTableId(prefilled);
            setLockChartSourceSelection(false);
            setShowChartConfig(true);
        },
        [availableChartTables],
    );

    const handleAddBlock = (toolId: string, scope?: { anchorRealId: string | null }) => {
        if (toolId === 'tool_test') {
            // A scoped add means "I am working on this instrument" — the next
            // one belongs right after it, not at the bottom of the section
            // where the user is not looking.
            if (scope?.anchorRealId) {
                const anchorRealId = scope.anchorRealId;

                openInstrumentSelector((newBlocks) => {
                    handleUpdate((prev) => {
                        const currentTests = prev.tests || { section_title: '', blocks: [] };
                        const blocks = currentTests.blocks || [];
                        const insertIndex = resolveScopeInsertIndex(blocks, anchorRealId);

                        return {
                            tests: {
                                ...currentTests,
                                blocks: [
                                    ...blocks.slice(0, insertIndex),
                                    ...newBlocks,
                                    ...blocks.slice(insertIndex),
                                ].map((block, index) => ({ ...block, order: index })),
                            },
                        };
                    });
                });
                return;
            }

            openInstrumentSelector();
            return;
        }

        if (toolId === 'tool_test_free') {
            openFreeInstrumentModal(scope);
            return;
        }

        if (toolId === 'tool_test_paste') {
            openPasteInstrumentModal(scope);
            return;
        }

        if (toolId === 'tool_chart') {
            openChartConfigFromToolbox(scope?.anchorRealId);
            return;
        }

        let createdBlockId: string | null = null;

        handleUpdate((prev) => {
            const currentTests = prev.tests || {
                section_title: '',
                blocks: [],
            };
            // Use the current blocks length from the previous state
            const blocks = currentTests.blocks || [];
            const newBlock = generateNewBlock(toolId, blocks.length);
            createdBlockId = (newBlock as { id?: string }).id || null;

            // Scoped adds land at the end of the anchor's group, so the block
            // appears where the user was working; unscoped adds append, as they
            // always have.
            const insertIndex = scope
                ? resolveScopeInsertIndex(blocks, scope.anchorRealId)
                : blocks.length;

            const updatedBlocks = [
                ...blocks.slice(0, insertIndex),
                newBlock,
                ...blocks.slice(insertIndex),
            ].map((block, index) => ({
                ...block,
                order: index,
            }));

            return {
                tests: {
                    ...currentTests,
                    blocks: updatedBlocks,
                },
            };
        });

        scrollPreviewToBlock(createdBlockId);

        // A text block is created empty and exists only to be written in, so it
        // opens with the caret already inside. The other types open their own
        // editors (instrument picker, chart wizard) or need no typing at all.
        if (toolId === 'tool_text' && createdBlockId) {
            requestBlockEdit?.(createdBlockId);
        }
    };

    const handleEditChartBlock = (block: TestBlock) => {
        if (block.block_type !== 'chart') return;

        setEditingChartBlock(block as ChartBlock);
        setChartSourceTableId((block as ChartBlock).linked_table_id || null);
        setLockChartSourceSelection(true);
        setShowChartConfig(true);
    };

    const handleSaveChartConfig = (config: ChartConfigValues) => {
        handleUpdate((prev) => {
            const currentTests = prev.tests || {
                section_title: '',
                blocks: [],
            };
            const blocks = currentTests.blocks || [];

            const sourceTable = blocks.find((block) => {
                return block.block_type === 'table' && block.id === config.linkedTableId;
            }) as TableBlock | undefined;

            if (!sourceTable) return {};

            const chartData = extractTableChartData(sourceTable);
            const selection = getSafeTableChartSelection(
                chartData,
                config.xAxisColumnId,
                config.yAxisColumnIds[0],
                config.measurementType,
            );

            if (!selection.xAxisColumnId || !selection.yAxisColumnId) {
                return {};
            }

            // Keep requested y columns that still exist; fall back to the safe default.
            const validYAxisColumnIds = config.yAxisColumnIds.filter((id) =>
                chartData.columns.some((column) => column.id === id),
            );
            const yAxisColumnIds =
                validYAxisColumnIds.length > 0 ? validYAxisColumnIds : [selection.yAxisColumnId];

            const measurementType =
                getColumnLabelById(chartData, yAxisColumnIds[0]) || config.measurementType;
            const references = (sourceTable.references || []).map((reference) => ({
                source_test_id: reference.source_test_id,
                source_provider: reference.source_provider,
            }));
            const datapoints = buildTableChartMultiDatapoints(sourceTable, {
                x_axis_column_id: selection.xAxisColumnId,
                y_axis_column_ids: yAxisColumnIds,
                skip_empty: true,
            });

            if (editingChartBlock) {
                const updatedBlocks = blocks.map((block) => {
                    const isMatch =
                        block.block_type === 'chart' && block.id === editingChartBlock.id;

                    if (!isMatch) return block;

                    return {
                        ...block,
                        automatic: false,
                        chart_type: config.chartType,
                        source_mode: 'linked_table',
                        title: config.title?.trim() || undefined,
                        measurement_type: measurementType,
                        linked_table_id: config.linkedTableId,
                        references,
                        source: {
                            x_axis_column_id: selection.xAxisColumnId,
                            y_axis_column_id: yAxisColumnIds[0],
                            y_axis_column_ids: yAxisColumnIds,
                            skip_empty: true,
                            series_colors: config.seriesColors,
                            reference_column_ids: config.referenceColumnIds,
                            band_color: config.bandColor,
                            series_symbols: config.seriesSymbols,
                            group_column_id: config.groupColumnId,
                            excluded_x_labels: config.excludedXLabels,
                            point_colors: config.pointColors,
                        },
                        reference_bands: config.referenceBands,
                        cutoff_lines: config.cutoffLines,
                        axis: config.axis,
                        quadrant: config.quadrant,
                        axis_end_labels: config.axisEndLabels,
                        line_style: config.lineStyle,
                        bar_style: config.barStyle,
                        // Stored only when disabled — undefined means "labels on" (the default).
                        point_labels: config.pointLabels === false ? false : undefined,
                        // Stored only when enabled — undefined means "single color family".
                        color_by_group: config.colorByGroup === true ? true : undefined,
                        color_by_classification:
                            config.colorByClassification === true ? true : undefined,
                        highlight: config.highlight,
                        show_deltas: config.showDeltas === true ? true : undefined,
                        datapoints: applySeriesColorsToDatapoints(
                            datapoints,
                            yAxisColumnIds,
                            config.seriesColors,
                        ),
                    } as ChartBlock;
                });

                return {
                    tests: {
                        ...currentTests,
                        blocks: updatedBlocks,
                    },
                };
            }

            const newChartBlock = createLinkedChartBlock(blocks.length, {
                ...config,
                linkedTableId: config.linkedTableId,
                xAxisColumnId: selection.xAxisColumnId,
                yAxisColumnIds,
                measurementType,
                datapoints,
            });
            newChartBlock.references = references;

            const updatedBlocks = insertChartAfterTable(
                blocks,
                config.linkedTableId,
                newChartBlock,
            ).map((block, index) => ({
                ...block,
                order: index,
            }));

            return {
                tests: {
                    ...currentTests,
                    blocks: updatedBlocks,
                },
            };
        });

        closeChartConfig();
    };

    return {
        showChartConfig,
        closeChartConfig,
        lockChartSourceSelection,
        editingChartBlock,
        selectedChartTableId: chartSourceTableId,
        availableChartTables,
        chartModalInitialConfig,
        handleAddTestsToBlock,
        openInstrumentSelector,
        handleAddChartFromTable,
        openChartConfigFromToolbox,
        handleAddBlock,
        handleEditChartBlock,
        handleSaveChartConfig,
    };
}
